import { usePermissions, useTranslate } from 'react-admin';
import CustomList from '@/components/global/list/CustomList.jsx';
import { PeriodFilter, ReferenceArrayFilter } from '@/components/global/filters/filters.jsx';
import { useMemo } from 'react';
import { TransactionSummaryAnalytics } from '@/components/analytics/TransactionSummaryAnalytics.jsx';
import { AnalyticsListActions } from '@/components/analytics/common/AnalyticsListActions';
import { DeviceUptimeAnalytics } from '@/components/analytics/DeviceUptimeAnalytics.jsx';
import { SubContent } from '@/components/analytics/common/SubContent.jsx';
import { TransactionTimeOfDayAnalytics } from '@/components/analytics/TransactionTimeOfDayAnalytics.jsx';
import { TransactionDurationAnalytics } from '@/components/analytics/TransactionDurationAnalytics.jsx';
import { ServiceClosedCount } from '@/components/analytics/ServiceClosedCount.jsx';
import { ServiceChangeAnalytics } from '@/components/analytics/ServiceChangeAnalytics.jsx';
import { ServiceRanking } from '@/components/analytics/ServiceRanking.jsx';
import CustomSelectArrayInput from '@/components/global/inputs/CustomSelectArrayInput.jsx';
import { provinceChoices } from '@/components/global/options/selectOptions';
import { useTheme, useMediaQuery } from '@mui/material';

const Filters = () => {
    const { permissions } = usePermissions();
    const translate = useTranslate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const baseFilters = useMemo(
        () => [
            <PeriodFilter key='period' source='period' alwaysOn={true} />,
            <ReferenceArrayFilter
                key='collectionPointId_in'
                source='collectionPointId_in'
                label={translate('common.collectionPoint')}
                optionText={choice => choice.customId}
                reference='collection-point'
                searchFields={['customId']}
                displayDeleteIcon={true}
                forceDesktopOnMobile={true}
                alwaysOn={isMobile}
            />,
            <ReferenceArrayFilter
                key='city'
                source='collectionPoint->address->city_in'
                reference='city'
                label={translate('resources.campaign.labels.cities')}
                forceDesktopOnMobile={true}
                alwaysOn={isMobile}
            />,
            <CustomSelectArrayInput
                key='province'
                source='collectionPoint->address->province_in'
                choices={provinceChoices(translate)}
                label={translate('resources.collectionPoint.form.voivodeship')}
                forceDesktopOnMobile={true}
                alwaysOn={isMobile}
            />,
        ],
        [isMobile, translate],
    );

    const adminFilters = useMemo(
        () => [
            ...baseFilters,
            <ReferenceArrayFilter
                key='groupId_in'
                source='groupId_in'
                reference='device-group'
                label={translate('resources.device.fields.group')}
                displayDeleteIcon={true}
                forceDesktopOnMobile={true}
                alwaysOn={isMobile}
            />,
            <ReferenceArrayFilter
                key='contractorId_in'
                source='contractorId_in'
                reference='contractor'
                label={translate('common.contractor')}
                displayDeleteIcon={true}
                forceDesktopOnMobile={true}
                alwaysOn={isMobile}
            />,
        ],
        [baseFilters, isMobile, translate],
    );

    return permissions?.isAdmin || permissions?.isBok ? adminFilters : baseFilters;
};

export const Dashboard = () => {
    const translate = useTranslate();

    return (
        <CustomList
            title={translate('menu.deviceUptimeAnalytics')}
            filters={Filters()}
            filterDefaultValues={{ period: '30' }}
            component='div'
            disablePagination={true}
            actions={<AnalyticsListActions />}
        >
            <TransactionSummaryAnalytics />
            <DeviceUptimeAnalytics />
            <SubContent first={<TransactionTimeOfDayAnalytics />} second={<TransactionDurationAnalytics />} />
            <SubContent
                first={
                    <>
                        <ServiceClosedCount />
                        <ServiceChangeAnalytics />
                    </>
                }
                second={<ServiceRanking />}
            />
        </CustomList>
    );
};
